import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { dialog } from '@/common/util'
import departamentosService from '@/services/departamentos.service'
import { departamentosServices } from '@/services/departamentos.service'

import type { DepartamentoType, DepartmentsBasicType, ListBasicType } from '@/types/types'

export const useDepartamentosStore = defineStore('departamentos', () => {
  const { t } = useI18n()

  /**
   * States
   */
  const list = ref<DepartamentoType[]>([])
  const basicList = ref<ListBasicType[]>([])
  const status = ref(0) // -1 erro de exceção; 0 - inicial; 1 - carregando; 2 - sucesso; 3 - erro não fatal

  const loading = computed(() => status.value === 1)

  /**
   * Getters
   */

  const lazyBasicList = computed(() => {
    if (status.value === 0) {
      getBasicList()
      return [{ value: '', label: 'Aguarde...' }]
    }

    return basicList.value
  })

  /**
   * Actions
   */
  const create = async (objData: DepartamentoType) => {
    status.value = 1
    try {
      const { data, success } = await departamentosService.create(objData)
      if (success) getAll()

      return data
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const update = async (objData: DepartamentoType, id: string) => {
    status.value = 1
    try {
      const { data, success } = await departamentosService.update<DepartamentoType>(id, objData)
      if (success) {
        const value = list.value.find(el => el.id === data.id)
        value && Object.assign(value, data)
      }

      return data
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const getAll = async (): Promise<void> => {
    status.value = 1

    try {
      const { data } = await departamentosService.get()
      list.value = data as DepartamentoType[]
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const remove = async (departamento: DepartamentoType, force?: boolean) => {
    status.value = 1

    if(!force) 
      return dialog.open({
        title: t('feedback.attention'),
        message: t('dialogs.delete.message', {type: t('labels.department').toLowerCase(), name: departamento.nome}),
        complete: () => remove(departamento, true)
      })

    try {
      const { success } = await departamentosService.remove(departamento.id)
      if (success) {
        list.value.splice(list.value.findIndex(el => el.id === departamento.id), 1)
      }

      return success
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const getById = async (id: string): Promise<DepartamentoType> => {
    try {
      const { data } = await departamentosService.get(id)
      return data as DepartamentoType
    } catch {
      //
    }
  }

  const exportDepartamentos = async () => {
    status.value = 1

    try {
      const csvFile = await departamentosService.exportCsv()
      return csvFile
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const getBasicList = (params?: DepartmentsBasicType) => {
    status.value = 1

    return departamentosServices.getListBasic({ ...params })
      .then(({ data }) => {
        basicList.value = (data || []).map(c => ({ value: c.id, label: c.nome }))
        status.value = 2
        return basicList.value
      })
      .catch(() => { status.value = 3 })
  }

  return {
    list,
    status,
    loading,
    basicList,
    //Getters
    lazyBasicList,
    // Actions
    create,
    update,
    getAll,
    getById,
    remove,
    getBasicList,
    exportDepartamentos
  }
})

export default useDepartamentosStore
